<template>
  <transition v-if="product && !(isOutOfStock || isComingSoon)" name="slide">
    <div
      v-if="top < 0"
      class="slide-down b-b b-grey-70 bg-white w-full fixed z-header <lg:hidden"
      :style="`top: ${pxToRem(header.sticky.offset)}`"
    >
      <div class="px-6 py-2 flex gap-4 items-center">
        <base-picture
          :alt="galleryItems[0].alt"
          class="shrink-0"
          height="80"
          :props-img="{ class: 'h-20 w-a' }"
          :src="galleryItems[0].src"
          width="64"
        />
        <div class="space-y-1">
          <p class="subtitle-2 truncate" data-dynatrace="product-name" data-test-id="product-name">
            {{ product.name }}
          </p>
          <product-pricing v-if="productPrice" class="body-3" :currency="product.currency" :price="productPrice" />
        </div>
        <vf-button v-if="!isSignInToBuy && !selectedVariant" class="ml-a shrink-0" @click="handleNextAttribute">
          {{ $t.selectSize }}
        </vf-button>
        <vf-button
          v-else
          class="ml-a shrink-0"
          :disabled="pending"
          :loading="addToCartLoading"
          @click="addToCart"
        >
          {{ isSignInToBuy ? $t.signInToBuy : $t.addToCart }}
        </vf-button>
      </div>
    </div>
  </transition>

  <teleport v-if="menuTeleportRef" to="[data-teleport='menu-buttons']">
    <vf-button
      class="ml-3 py-$py b grow ws-nowrap relative"
      disable-success-icon
      :disabled="!header.expanded && !isNotifyMe && !isProductOrInventoryPending && (isComingSoon || isOutOfStock)"
      :loading="!header.expanded && (addToCartLoading || isProductOrInventoryPending)"
      style="
        background-color: #323334;
        border-color: #4b4d4e;
        border-radius: 0.5rem;
        box-shadow: inset 0 1px 1px 0 rgba(188, 187, 187, 0.1), 0 4px 10px 0 rgba(23, 26, 28, 0.2);
        width: 3.25rem;
        --py: 0.875rem
      "
      variant="primary"
      @click="handleClick"
    >
      <span v-if="!header.expanded" class="flex center absolute-0">
        {{ ctaLabel }}
      </span>
      <span v-else class="sr-only">{{ $t.mobileNavigationMinimize }}</span>
      <vf-icon :class="{ 'op-0': !header.expanded }" name="close" size="md" />
    </vf-button>
  </teleport>
  <product-notify-me ref="notifyMeRef" class="hidden" />
  <panel-variant-selection size="sm" />
</template>

<script lang="ts" setup>
import type { DialogVariantSelection as DialogVariantSelectionProps, ProductNotifyMe } from '#components'

const header = useHeaderStore()
const { $gtm, $t } = useNuxtApp()

const {
  addToCart,
  addToCartLoading,
  attributeSelectionLabels,
  findInStore,
  galleryItems,
  isComingSoon,
  isProductOrInventoryPending,
  isNotifyMe,
  isOutOfStock,
  isRobisOnly,
  isSignInToBuy,
  nextAttribute,
  pending,
  product,
  productPrice,
  selectedVariant
} = usePdp()
const menuTeleportRef = useState<HTMLElement>('menuTeleportRef')
const stickyState = useSticky()
const { top } = useElementBounding(() => stickyState.value.productDetailsCta)

const PanelVariantSelection = createPanel('variant-selection')
const notifyMeRef = ref<InstanceType<typeof ProductNotifyMe>>()

const ctaLabel = computed(() => {
  if (isNotifyMe.value && (isOutOfStock.value || isComingSoon.value)) return $t.notifyMe
  if (isComingSoon.value) return $t.comingSoon
  if (isOutOfStock.value) return $t.outOfStock
  if (isSignInToBuy.value) return $t.signInToBuy
  if (isRobisOnly.value) return $t.reserveInStore
  if (!selectedVariant.value) return $t.selectSize
  return $t.addToCart
})

const handleNextAttribute = (options: Omit<typeof DialogVariantSelectionProps, 'attribute'> = {}) => {
  if (!nextAttribute.value) return

  PanelVariantSelection.open({ attribute: nextAttribute.value, ...options })
  throw new Error('Select Next Attribute')
}

const handleReserveInStore = () => {
  try {
    handleNextAttribute({ enableReserveInStore: true })
    findInStore(attributeSelectionLabels.value, 'reserve')
  }
  catch (e) {
    console.info(e)
  }
}

const handleClick = () => {
  if (header.expanded) {
    header.expanded = false
    if (product.value) $gtm.push('pdpPage.onMobileNavigationInteraction', product.value.id, false)
  }
  else if (isNotifyMe.value && (isOutOfStock.value || isComingSoon.value)) {
    notifyMeRef.value?.openNotifyMe()
  }
  else if (isRobisOnly.value) {
    handleReserveInStore()
  }
  else if (!selectedVariant.value && !isSignInToBuy.value) {
    handleNextAttribute()
  }
  else {
    addToCart()
  }
}

onMounted(async () => {
  await until(pending).toBe(false)
  await wait(500)
  header.expanded = false
})
</script>
